import { useState } from "react";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import SectionHeading from "@/components/common/SectionHeading";
import { CONTACT, FORM_ENDPOINT } from "@/lib/data";

export default function ContactForm({ compact = false }) {
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    setSending(true);
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });
      if (res.ok) {
        toast.success("Message sent — we'll be in touch within 24 hours.");
        form.reset();
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className={`${compact ? "" : "py-24 sm:py-32"}`} data-testid="contact-form-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {!compact && (
          <SectionHeading
            eyebrow="Talk to us"
            title="Let's build something together."
            sub="Fill in the form or reach out directly. A senior consultant will respond within 24 hours."
          />
        )}

        <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 ${!compact ? "mt-16" : ""}`}>
          <div className="space-y-8">
            <div>
              <p className="font-inter text-[11px] uppercase tracking-[0.24em] text-[#10B981] font-semibold mb-3">Reach us</p>
              <h3 className="font-poppins text-2xl sm:text-3xl text-white font-medium tracking-tight">
                Prefer direct contact? We're one message away.
              </h3>
            </div>

            <div className="space-y-4">
              {[
                { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
                { icon: Phone, label: "Phone / WhatsApp", value: CONTACT.phone, href: `tel:${CONTACT.phoneClean}` },
                { icon: MapPin, label: "Office", value: `${CONTACT.addressLine1}, ${CONTACT.addressLine2}`, href: "https://maps.google.com/?q=5830+E+2nd+St+Casper+Wyoming+82609" },
              ].map((c) => (
                <a
                  key={c.label}
                  href={c.href}
                  className="flex items-center gap-4 p-4 rounded-2xl glass hover:border-[#10B981]/40 transition-colors"
                >
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: "linear-gradient(135deg, rgba(30,58,138,0.4), rgba(16,185,129,0.4))" }}>
                    <c.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-inter text-[11px] uppercase tracking-[0.16em] text-white/40">{c.label}</p>
                    <p className="font-poppins text-white text-[15px] font-medium">{c.value}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          <form onSubmit={submit} className="glass rounded-3xl p-6 sm:p-8 space-y-4" data-testid="contact-form">
            <input type="hidden" name="_captcha" value="false" />
            <input type="hidden" name="_subject" value="New enquiry from SKVY website" />
            <input type="hidden" name="_template" value="table" />

            <div className="grid sm:grid-cols-2 gap-4">
              <Field name="name" label="Full name" placeholder="Your name" required />
              <Field name="email" label="Email" type="email" placeholder="you@company.com" required />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field name="phone" label="Phone" placeholder="+91 …" />
              <Field name="company" label="Company" placeholder="Company name" />
            </div>
            <div>
              <label className="font-inter text-[11px] uppercase tracking-[0.16em] text-white/50">I am a</label>
              <select
                name="role"
                required
                data-testid="contact-role"
                defaultValue=""
                className="w-full mt-2 bg-[#0A1023]/60 border border-white/10 rounded-xl px-4 py-3 text-white font-inter text-sm focus:border-[#10B981] focus:outline-none"
              >
                <option value="" disabled>Select…</option>
                <option value="opt">Student on OPT / STEM OPT</option>
                <option value="cpt">Student on CPT / F-1</option>
                <option value="h1b">H-1B Professional</option>
                <option value="fte">US Citizen / GC Holder</option>
                <option value="employer">Employer / Hiring Partner</option>
              </select>
            </div>
            <div>
              <label className="font-inter text-[11px] uppercase tracking-[0.16em] text-white/50">How can we help?</label>
              <textarea
                name="message"
                rows={4}
                required
                data-testid="contact-message"
                placeholder="Share your target role, tech stack, timeline and visa status…"
                className="w-full mt-2 bg-[#0A1023]/60 border border-white/10 rounded-xl px-4 py-3 text-white font-inter text-sm focus:border-[#10B981] focus:outline-none resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="w-full inline-flex items-center justify-center gap-2 text-white font-inter font-medium text-sm px-6 py-3.5 rounded-full disabled:opacity-60"
              style={{ background: "linear-gradient(120deg, #1E3A8A, #10B981)", boxShadow: "0 20px 40px -12px rgba(16,185,129,0.5)" }}
            >
              {sending ? "Sending…" : (<>Send Message <ArrowRight className="w-4 h-4" /></>)}
            </button>
            <p className="font-inter text-[11px] text-white/40 text-center">
              By submitting you agree to our privacy policy. No spam, ever.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ name, label, type = "text", placeholder, required }) {
  return (
    <div>
      <label className="font-inter text-[11px] uppercase tracking-[0.16em] text-white/50">{label}{required && " *"}</label>
      <input
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        data-testid={`contact-${name}`}
        className="w-full mt-2 bg-[#0A1023]/60 border border-white/10 rounded-xl px-4 py-3 text-white font-inter text-sm placeholder:text-white/30 focus:border-[#10B981] focus:outline-none"
      />
    </div>
  );
}
